// Real ThemeStudios content lifted verbatim or near-verbatim from themestudios.in
// so the entire site is grounded in actual studio copy, not template fillers.

import { BOARDS } from "@/constants/assets";

export const HERO = {
  eyebrow: "ThemeStudios Digital Designers · Madurai",
  headline1: "Posters, reels, branding, ads",
  headline2: "and monthly creative systems",
  headline3: "for businesses that need to",
  headline4: "stay visible every day.",
  sub: "ThemeStudios supports one-time creatives, brand identity work, ads support, business profile design, and industry-specific monthly packs for restaurants, salons, clinics, retail stores, startups, education brands, and service businesses.",
  bullets: [
    "Posters, reels, branding, ads support and monthly systems in one studio",
    "64+ active services and packs across universal and industry-specific lanes",
    "WhatsApp-first briefing for faster order starts",
    "Original layouts built for practical small-business promotion",
  ],
  primaryCta: { label: "Explore service catalog", testid: "hero-cta-catalog" },
  secondaryCta: { label: "Ask for the right package", testid: "hero-cta-package" },
};

export const STATS = [
  { k: "64+", v: "Active services & packs" },
  { k: "₹349", v: "Starting service price" },
  { k: "7", v: "Industry verticals" },
  { k: "₹24,999", v: "Top monthly support" },
];

// 5 CORE SERVICES — exact wording from themestudios.in
export const CORE_SERVICES = [
  {
    n: "01",
    count: "5 active services",
    title: "Posters & offer creatives",
    price: "₹399 — ₹1,499",
    blurb:
      "Business posters, sale creatives, premium campaign posters, and seasonal or occasion-led visuals for everyday promotion.",
    to: "/services/posters-social",
  },
  {
    n: "02",
    count: "5 active services",
    title: "Social posts, stories & catalog visuals",
    price: "₹349 — ₹1,499",
    blurb:
      "Single posts, WhatsApp status creatives, carousels, cover banners, and catalog-style visuals for social-first businesses.",
    to: "/services/posters-social",
  },
  {
    n: "03",
    count: "5 reel services",
    title: "Reels, covers & motion promos",
    price: "₹499 — ₹3,499",
    blurb:
      "Reel covers, animated business reels, festival reels, and stronger ad-style motion creatives for campaigns that need more attention.",
    to: "/services/reels-motion",
  },
  {
    n: "04",
    count: "12 branding offers",
    title: "Brand identity, stationery & launch kits",
    price: "₹999 — ₹29,999",
    blurb:
      "Logo design, stationery, industry-specific branding kits, startup launch packs, and packaged identity support for growing brands.",
    to: "/services/branding-identity",
  },
  {
    n: "05",
    count: "10 support offers",
    title: "Ads, profile support & website visuals",
    price: "₹699 — ₹5,999",
    blurb:
      "Google Business creatives, profile PDFs, website hero banners, ad posters, reel ads, and setup support for paid promotion.",
    to: "/services/ads-profile",
  },
  {
    n: "06",
    count: "27 monthly packs",
    title: "Monthly social and campaign systems",
    price: "₹4,999 — ₹24,999 / month",
    blurb:
      "Recurring support packs across universal, clinic, restaurant, startup, salon, education, retail, and construction categories — including builder, catalog, and institute-focused pack lanes.",
    to: "/services/monthly-systems",
  },
];

// 4-step workflow — exact ThemeStudios wording
export const WORKFLOW = [
  {
    n: "01",
    title: "Send the brief",
    body:
      "Share the business name, logo, offer details, product images, preferred language, platform and deadline through WhatsApp.",
  },
  {
    n: "02",
    title: "Match the right service lane",
    body:
      "ThemeStudios confirms whether a one-time creative, branding kit, ads support item, monthly plan, or industry pack fits the work best.",
  },
  {
    n: "03",
    title: "Build the creative",
    body:
      "We shape the visual hierarchy for mobile-first promotion with the right mix of offer copy, branding cues, motion, and campaign structure.",
  },
  {
    n: "04",
    title: "Revise and deliver",
    body:
      "Minor revisions are handled within the confirmed scope before final JPG, PNG, PDF, or campaign-ready delivery.",
  },
];

// 5-step order flow — exact wording from the live site
export const ORDER_FLOW = [
  { n: "01", text: "Share your brief on WhatsApp" },
  { n: "02", text: "We confirm package, scope, price, and timeline" },
  { n: "03", text: "Payment is requested only through official payment links or approved UPI details" },
  { n: "04", text: "Draft is shared for review" },
  { n: "05", text: "Final files are delivered after approval" },
];

// Trust strip — exact disclaimers from the live site
export const TRUST_NOTES = [
  "ThemeStudios never asks for card details in WhatsApp chat.",
  "Payments are requested only after package and scope confirmation.",
  "Official support happens through the listed WhatsApp number and support email.",
];

// 3 working principles — actual studio standards from the live site
export const PRINCIPLES = [
  {
    t: "Original layouts",
    b: "Every creative is shaped fresh for the brand and the brief — never lifted from generic template galleries.",
  },
  {
    t: "Client-provided assets only",
    b: "Photos, logos and references come from the client or licensed sources, so the final output stays brand-accurate and rights-safe.",
  },
  {
    t: "Copyright-safe brief requirements",
    b: "We won't add fonts, music, photos or footage that put the business at legal risk. Quiet rigour over flashy shortcuts.",
  },
];

// 7 real industry verticals with exact pricing ranges
export const INDUSTRIES = [
  {
    name: "Restaurant & food brands",
    range: "₹6,999 — ₹16,999",
    body:
      "Food promo posters, offer pushes, reel covers, menu-led branding, and monthly social systems for cafes, takeaways, and dine-in brands.",
    img: BOARDS.restaurantSalon,
  },
  {
    name: "Salon & beauty brands",
    range: "₹6,999 — ₹16,999",
    body:
      "Service promotion, bridal or festive pushes, reel covers, offer creatives, and packaged branding for salons and beauty studios.",
    img: BOARDS.restaurantSalon,
  },
  {
    name: "Clinics & healthcare",
    range: "₹6,999 — ₹19,999",
    body:
      "Trust-building posters, doctor or service intro creatives, awareness content, ad support, and healthcare-focused branding kits.",
    img: BOARDS.clinicStartup,
  },
  {
    name: "Retail & local shops",
    range: "₹4,999 — ₹9,999",
    body:
      "Offer posters, festival pushes, stories, monthly retail promotion, and catalog-style creative systems for local stores and boutiques.",
    img: BOARDS.constructionRetail,
  },
  {
    name: "Education & training",
    range: "₹6,999 — ₹17,999",
    body:
      "Admission posters, course creatives, institute branding support, enquiry pushes, reel covers, and monthly content systems for training centres.",
    img: BOARDS.education,
  },
  {
    name: "Real estate & construction",
    range: "₹7,999 — ₹24,999",
    body:
      "Property posters, before-and-after content, service introductions, builder branding, brochures, and construction-focused monthly promotion support.",
    img: BOARDS.constructionRetail,
  },
  {
    name: "Startups & launch brands",
    range: "₹9,999 — ₹29,999",
    body:
      "Launch kits, brand identity packs, social creatives, story support, and startup-oriented monthly growth systems for early-stage brands.",
    img: BOARDS.clinicStartup,
  },
];

// 7 real FAQs — exact ThemeStudios Q/A
export const FAQS = [
  {
    q: "How do I choose the right service or package?",
    a: "Start on WhatsApp or use the website enquiry form, share your business type, goal, preferred format, and timeline, and ThemeStudios will suggest the right one-time service, monthly pack, or industry package before work starts.",
  },
  {
    q: "What is the current price range?",
    a: "Active public offers currently start from story and status design at ₹349, run through posters, reels, branding, and ads support, and go up to premium launch or full-support packs at the higher end.",
  },
  {
    q: "Do you offer only posters?",
    a: "No. ThemeStudios also handles social posts, stories, carousels, reel creatives, logo and branding kits, business profile PDFs, ad creatives, and monthly support systems.",
  },
  {
    q: "Are monthly plans available for different industries?",
    a: "Yes. There are monthly packs for universal business use as well as clinic, startup, restaurant, salon, education, retail, and real-estate categories.",
  },
  {
    q: "How does ads support pricing work?",
    a: "Creative and setup fees are quoted separately from ad spend. ThemeStudios can handle ad posters, ad reels, setup support, and monthly creative management, but the media budget itself is extra.",
  },
  {
    q: "How many revisions are included?",
    a: "Revision counts depend on the selected service or package. Small changes are included inside the stated scope, while new directions or redesign-level changes are handled as extra work.",
  },
  {
    q: "What assets should I provide with the first brief?",
    a: "Share your business name, contact number, offer details, logo, photos or references, preferred language, target platform, and delivery deadline so the right scope can be confirmed quickly.",
  },
];

// Smart WhatsApp deep-link helper with prefilled brief template
export const wa = (intent = "package") => {
  const briefTemplate =
    "Hi ThemeStudios, I want to start a design order.\n\nBusiness name:\nLogo available:\nOffer details or poster text:\nContact number:\nProduct or service photo:\nLanguage (Tamil / English / Both):\nPlatform (WhatsApp / Instagram / Facebook):\nPreferred style:\nDelivery time:";
  const packageTemplate =
    "Hi ThemeStudios, I need the right service or package for my business.\n\nBusiness name:\nBusiness type:\nRequired service or package:\nWhat should the creative support help with?";
  const msg = intent === "brief" ? briefTemplate : packageTemplate;
  return `https://wa.me/918903205676?text=${encodeURIComponent(msg)}`;
};
